import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, ToggleLeft, ToggleRight, Plus, Pencil, Trash2 } from "lucide-react";
import { authHeader } from "@/hooks/useAdminAuth";
import API from "@/lib/api";
import AdminFormModal, { Field, TextInput, NumberInput, TextArea } from "@/components/admin/AdminFormModal";
import ImageUpload from "@/components/admin/ImageUpload";

const EMPTY = {
  title: "",
  category: "",
  summary: "",
  content: "",
  image_url: "",
  read_time: 5,
  is_active: true,
};

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/blogs?admin=true`)
      .then(({ data }) => setBlogs(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      title: b.title || "",
      category: b.category || "",
      summary: b.summary || "",
      content: b.content || "",
      image_url: b.image_url || "",
      read_time: b.read_time ?? 5,
      is_active: b.is_active ?? true,
    });
    setModalOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, read_time: Number(form.read_time) || 5 };
      if (editing) {
        await axios.put(`${API}/blogs/${editing.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/blogs`, payload, { headers: authHeader() });
      }
      setModalOpen(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, current) => {
    try {
      await axios.put(`${API}/blogs/${id}`, { is_active: !current }, { headers: authHeader() });
      setBlogs((bs) => bs.map((b) => (b.id === id ? { ...b, is_active: !current } : b)));
    } catch (err) {
      console.error(err);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete blog post "${b.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/blogs/${b.id}`, { headers: authHeader() });
      setBlogs((bs) => bs.filter((x) => x.id !== b.id));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7B2CBF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-display">Blog Posts</h1>
          <p className="text-gray-500 mt-1 text-sm">{blogs.length} articles</p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-[#00A8E8] to-[#7B2CBF] text-white hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      <div className="space-y-3">
        {blogs.map((b) => (
          <div
            key={b.id}
            className={`bg-[#0D0E13] border border-white/5 rounded-xl overflow-hidden flex gap-4 p-4 transition-opacity ${
              b.is_active ? "" : "opacity-50"
            }`}
          >
            {b.image_url && (
              <div className="w-24 h-16 rounded-lg overflow-hidden shrink-0">
                <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#7B2CBF]/15 text-[#7B2CBF] border border-[#7B2CBF]/25 font-medium">
                    {b.category}
                  </span>
                  <h3 className="text-white text-sm font-medium mt-1.5 line-clamp-2">{b.title}</h3>
                  <div className="flex items-center gap-1 mt-1 text-gray-500 text-xs">
                    <Clock className="w-3 h-3" />
                    <span>{b.read_time} min read</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                      b.is_active
                        ? "bg-green-400/15 text-green-400 border-green-400/25"
                        : "bg-gray-500/15 text-gray-400 border-gray-500/25"
                    }`}
                  >
                    {b.is_active ? "Active" : "Hidden"}
                  </span>
                  <button
                    onClick={() => toggleActive(b.id, b.is_active)}
                    className="text-gray-500 hover:text-white transition-colors p-1"
                    title={b.is_active ? "Hide" : "Show"}
                  >
                    {b.is_active ? (
                      <ToggleRight className="w-6 h-6 text-green-400" />
                    ) : (
                      <ToggleLeft className="w-6 h-6" />
                    )}
                  </button>
                  <button
                    onClick={() => openEdit(b)}
                    className="text-gray-500 hover:text-[#00A8E8] transition-colors p-1"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(b)}
                    className="text-gray-500 hover:text-red-400 transition-colors p-1"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <AdminFormModal
        open={modalOpen}
        title={editing ? "Edit Blog Post" : "New Blog Post"}
        onClose={() => setModalOpen(false)}
        onSubmit={submit}
        submitting={saving}
        submitLabel={editing ? "Update Post" : "Create Post"}
      >
        <Field label="Title" required>
          <TextInput value={form.title} onChange={(e) => update("title", e.target.value)} required />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Category" required>
            <TextInput
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              placeholder="e.g. Agentic AI"
              required
            />
          </Field>
          <Field label="Read Time (min)">
            <NumberInput value={form.read_time} onChange={(e) => update("read_time", e.target.value)} min="1" />
          </Field>
        </div>
        <Field label="Summary" required>
          <TextArea value={form.summary} onChange={(e) => update("summary", e.target.value)} required />
        </Field>
        <Field label="Content" hint="Optional — full article body">
          <TextArea rows={6} value={form.content} onChange={(e) => update("content", e.target.value)} />
        </Field>
        <Field label="Image" hint="Upload a file or paste a URL">
          <ImageUpload value={form.image_url} onChange={(v) => update("image_url", v)} />
        </Field>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => update("is_active", e.target.checked)}
            className="w-4 h-4 accent-[#7B2CBF]"
          />
          <span className="text-sm text-white">Active (visible on site)</span>
        </label>
      </AdminFormModal>
    </div>
  );
}
